import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { COUNTRIES } from "../data/content";
import { SectionHeading, stagger, fadeUp } from "../lib/motion";
import { t } from "../i18n";

export const CountryCard = ({ country }) => {
  const navigate = useNavigate();
  return (
    <motion.div
      variants={fadeUp}
      onClick={() => navigate(`/countries/${country.slug}`)}
      data-testid={`country-card-${country.slug}`}
      className="group relative rounded-2xl overflow-hidden cursor-pointer h-80 border border-white/10 transition-all duration-500 hover:border-gold/50"
    >
      <img
        src={country.image}
        alt={country.name}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/75 to-navy-deep/15" />
      <div className="absolute top-4 left-4 flex items-center gap-2 glass-dark rounded-full px-3 py-1.5 border border-white/10">
        <img
          src={`https://flagcdn.com/w40/${country.code}.png`}
          alt={`${country.name} flag`}
          className="h-3.5 w-5 object-cover rounded-[2px]"
          loading="lazy"
        />
        <span className="text-[11px] font-bold tracking-[0.15em] text-slate-200 font-display uppercase">{country.code}</span>
      </div>
      <div className="absolute bottom-0 inset-x-0 p-6">
        <h3 className="font-display text-2xl font-bold text-white mb-1.5">{t(`countries.names.${country.slug}`, country.name)}</h3>
        <p className="text-sm text-slate-300/90 leading-snug mb-4">{t(`countries.blurbs.${country.slug}`, country.blurb)}</p>
        <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.2em] text-gold font-display opacity-0 translate-y-2 transition-all duration-400 group-hover:opacity-100 group-hover:translate-y-0">
          {t("countries.explore")} <ArrowUpRight className="h-4 w-4" />
        </span>
      </div>
    </motion.div>
  );
};

const CountriesSection = () => (
  <section id="countries" data-testid="countries-section" className="relative py-24 lg:py-32 bg-navy-deep">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading
        eyebrow={t("countries.eyebrow")}
        title={t("countries.title")}
        description={t("countries.desc")}
        testid="countries-heading"
      />
      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-60px" }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {COUNTRIES.map((c) => (
          <CountryCard key={c.slug} country={c} />
        ))}
      </motion.div>
    </div>
  </section>
);

export default CountriesSection;
